// ─── ERP-5289 Trade-In Enhancement: endpoints of the new screens ──────────────
// The new Trade-In UI went to stage before its backend existed, so every call the new screens
// make goes through this file. Real now: the Listing Excel import (download → upload →
// validate → save), the WooCommerce sync banner after it, and the Listing Image library
// (list, check names, import, delete — ERP-5297 / ERP-5317 / ERP-5318). What is still mocked
// is async and returns the shape we expect from the real endpoint, so wiring the backend
// later is a change to this file only.
//
// The mock persists nothing. Its state lives in module memory: it survives navigating around
// the app, and resets on a page reload.

import apiHelper from '../../apiHelper';
import downloadXlsx from '../../utils/downloadXlsx';
import { DEVICE_DEFAULT_IMAGES } from './tradeInImages.js';

// ─── Listing Image library (ERP-5297) ─────────────────────────────────────────
// The bundled device placeholders are the picker's system images: always offered, never
// stored in the library, and picking the current device's one keeps today's "device
// default" semantics. Their real filenames are listed here because a bundled asset's URL is
// content-hashed.
export const DEVICE_DEFAULT_FILENAMES = {
    phone: 'Mister-Mobile-Placeholder-1.png',
    tablet: 'mister-mobile-tablet.png',
    watch: 'Mister-Mobile-Watch.png',
    accessories: 'Apple-Earbuds-Placeholder-1.png',
};

const SYSTEM_IMAGES = Object.entries(DEVICE_DEFAULT_IMAGES).map(([device, url]) => ({
    id: `system-${device}`,
    name: DEVICE_DEFAULT_FILENAMES[device],
    url,
    file: null,
    device,
    isSystem: true,
    usedListings: null,
    usedItems: null,
    modifiedAt: null,
}));

const toLibraryRow = (r) => ({
    id: r.id,
    name: r.name,
    url: r.url,
    file: null,
    device: null,
    isSystem: false,
    usedListings: r.used_listings || 0,
    usedItems: r.used_items || 0,
    // Items linked to it at all, archived ones included — any link blocks the delete.
    linkedItems: r.linked_items || 0,
    modifiedAt: r.modified_at,
});

async function errorMessage(res, fallback) {
    try {
        const data = await res.json();
        return data?.detail || data?.message || fallback;
    } catch (e) {
        return fallback;
    }
}

// GET sales/trade-in-images/?search=&ordering=&page=&page_size=
// `includeSystem` puts the device placeholders first — the picker wants them, the Listing
// Image tab only lists what is actually stored.
export async function listLibraryImages({ search = '', ordering = '-modified_at', page = 1, pageSize = 20, includeSystem = false } = {}) {
    const params = new URLSearchParams({ search: search.trim(), ordering, page, page_size: pageSize });
    const res = await apiHelper(`sales/trade-in-images/?${params}`, 'GET');
    if (!res?.ok) throw new Error(await errorMessage(res, 'Failed to load images'));
    const data = await res.json();
    const rows = (data.results || []).map(toLibraryRow);
    if (!includeSystem) return { results: rows, count: data.count || 0 };
    const q = search.trim().toLowerCase();
    const system = SYSTEM_IMAGES.filter(r => r.name.toLowerCase().includes(q));
    return { results: [...system, ...rows], count: (data.count || 0) + system.length };
}

// The device placeholders whose name matches `search` (the picker shows them as local tiles).
export const systemLibraryImages = (search = '') => {
    const q = search.trim().toLowerCase();
    return SYSTEM_IMAGES.filter(r => r.name.toLowerCase().includes(q));
};

// GET sales/trade-in-images/?search=&offset=&limit=&locate_name=&locate_file=
// Row-based paging for the image picker, which puts its own tiles ahead of the library.
// `limit: 0` fetches just the count. With `locateName` / `locateFile`, `located` says where
// that image sits in the results ({ id, index }), or is null when it isn't there.
export async function listLibraryRange({ search = '', ordering = '-modified_at', offset = 0, limit = 0, locateName = '', locateFile = '' } = {}) {
    const params = new URLSearchParams({ search: search.trim(), ordering, offset, limit });
    if (locateName) params.set('locate_name', locateName);
    if (locateFile) params.set('locate_file', locateFile);
    const res = await apiHelper(`sales/trade-in-images/?${params}`, 'GET');
    if (!res?.ok) throw new Error(await errorMessage(res, 'Failed to load images'));
    const data = await res.json();
    return { results: (data.results || []).map(toLibraryRow), count: data.count || 0, located: data.located || null };
}

// POST sales/trade-in-images/check-names/
// Image names are unique across the whole library, case-insensitively, and against every
// other trade-in file, since they all share one server folder. The listing being edited is
// excluded, so its own files don't count against it.
//
// Fails open, like fetchTakenFilenames: the import endpoint re-checks every name anyway.
export async function findTakenImageNames(names, excludeListingId = null) {
    if (!names.length) return new Set();
    const res = await apiHelper('sales/trade-in-images/check-names/', 'POST', {
        names,
        exclude_listing_id: excludeListingId || null,
    });
    if (!res?.ok) return new Set();
    const data = await res.json();
    return new Set(Array.isArray(data?.taken) ? data.taken : []);
}

// POST sales/trade-in-images/import/   (multipart: one `images` part per file + its `names`)
// uploadSource: 'import' (Listing Image tab) or 'picker' (Listing Item image picker).
export async function importLibraryImages(entries, excludeListingId = null, uploadSource = 'import') {
    const form = new FormData();
    entries.forEach(({ file, name }) => {
        form.append('images', file, file.name);
        form.append('names', name);
    });
    if (excludeListingId) form.append('exclude_listing_id', excludeListingId);
    form.append('upload_source', uploadSource);
    const res = await apiHelper('sales/trade-in-images/import/', 'POST', form, 'multipart/form-data');
    if (!res?.ok) {
        let message = 'Image import failed';
        try {
            const data = await res.json();
            if (Array.isArray(data?.errors) && data.errors.length) {
                message = data.errors.map(e => `${e.name}: ${e.error}`).join('\n');
            } else {
                message = data?.detail || message;
            }
        } catch (e) { /* keep the generic message */ }
        const err = new Error(message);
        err.status = res?.status;
        throw err;
    }
    const data = await res.json();
    return (data.results || []).map(toLibraryRow);
}

// DELETE sales/trade-in-images/<id>/   — 409 while a Listing Item still uses the image.
export async function deleteLibraryImage(id) {
    const res = await apiHelper(`sales/trade-in-images/${id}/`, 'DELETE');
    if (!res?.ok) throw new Error(await errorMessage(res, 'Image is in use and cannot be deleted'));
}

// ─── Listing Excel import ─────────────────────────────────────────────────────
// All real (ERP-5307 / 5310 / 5311 / 5314): the server classifies each row as new / updated /
// deleted, marks it Success or Fatal, and the batch it returns is already 'validated'. Save
// applies it and starts the WooCommerce sync the banner below follows.
const IMPORT_BATCH = 'sales/trade-in-listings/import/upload-batch/';

// Real endpoint (ERP-5307): the Creation Template — every active Listing Item with its current
// Base Price and Deductions, New rows before Used, plus an SPU lookup sheet.
export async function downloadListingTemplate() {
    return downloadXlsx('sales/trade-in-listings/import/template/', 'Trade-In Listing Template.xlsx');
}

async function readBatchResponse(res, fallback) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.message || fallback);
    return data;
}

// Real endpoint (ERP-5310). Rejections (wrong columns, no rows, not an .xlsx) carry a `detail`
// the page shows as the upload error.
export async function uploadListingImport(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await apiHelper(IMPORT_BATCH, 'POST', form, 'multipart/form-data');
    return readBatchResponse(res, 'Upload failed — please try again');
}

// Real endpoint (ERP-5310): { id, status, summary, issue_count, issues }.
export async function getListingImportBatch(id) {
    const res = await apiHelper(`${IMPORT_BATCH}${id}/`, 'GET');
    return readBatchResponse(res, 'Import not found');
}

// Real endpoint (ERP-5311): the uploaded Excel with Result (Success / Fatal) and Reason in front.
export async function downloadListingImportValidation(id) {
    return downloadXlsx(`${IMPORT_BATCH}${id}/validation-result/`, 'validation.xlsx');
}

// ─── WooCommerce sync (banner on the Trade-In Listing page) ───────────────────
// Real (ERP-5315): after Save the server sends the created / updated / deleted Listing Items to
// WooCommerce (and the Trade-In Calculator Google Sheet) in the background. The banner shows the
// newest saved import nobody has closed; Try Again re-sends the failed items, 3 attempts in all.
const RUN_LABELS = { new: 'Creating New', updated: 'Updating', deleted: 'Deleting' };

// Real endpoint (ERP-5314): applies the batch — creates, updates, soft-deletes, relists and
// reorders — and queues the sync. Fatal rows were never part of it.
export async function completeListingImport(id) {
    const res = await apiHelper(`${IMPORT_BATCH}${id}/complete/`, 'POST', {});
    return readBatchResponse(res, 'Save failed — please try again');
}

// phase: 'run' while pushing, then 'done' (all synced) or 'error' (some failed).
const toSync = (data) => ({
    id: data.id,
    phase: data.running ? 'run' : (data.failed ? 'error' : 'done'),
    groups: data.groups.map(g => ({
        key: g.key, label: g.label, runLabel: RUN_LABELS[g.key] || g.label,
        total: g.total, done: g.done, ok: g.success, fail: g.failed,
    })),
    total: data.total,
    ok: data.success,
    fail: data.failed,
    attemptsRemaining: data.retries_left,
});

export async function getActiveListingSync() {
    const res = await apiHelper(`${IMPORT_BATCH}active/`, 'GET');
    if (!res || !res.ok) return null;
    const data = await res.json().catch(() => null);
    return data && data.id ? toSync(data) : null;
}

export async function retryListingSync(id) {
    await apiHelper(`${IMPORT_BATCH}${id}/retry/`, 'POST', {});
}

export async function dismissListingSync(id) {
    await apiHelper(`${IMPORT_BATCH}${id}/dismiss/`, 'POST', {});
}

// The Validation Result with a Sync Result column added; deleted items are listed under the rows.
export async function downloadListingSyncReport(id) {
    return downloadXlsx(`${IMPORT_BATCH}${id}/report/`, 'Trade-In Listing Import Report.xlsx');
}
